import React, { useState, useRef, useEffect, useContext } from "react";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import EmailService, {
  MailProvider,
  SendEmailPayload,
  SaveDraftPayload,
} from "./email.service";
import { AuthContext } from "../../../../context/authcontext";
import { showErrorAlert, showSuccessAlert } from "../../../../utils/alertUtils";
import AttachmentComponent from "../../../../components/AttachmentComponent";

interface ComposeEmailProps {
  cachedProviders?: MailProvider[];
  isOpen: boolean;
  onClose: () => void;
  onMinimize: () => void;
  draftData?: any;
  readOnlyRecipient?: boolean;
  crm_entity_id?: number;
  record_id?: number;
}

interface EmailChip {
  email: string;
  isValid: boolean;
}

const ComposeEmail: React.FC<ComposeEmailProps> = ({
  isOpen,
  onClose,
  onMinimize,
  draftData,
  readOnlyRecipient = false,
  crm_entity_id,
  record_id,
  cachedProviders,
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [recipientChips, setRecipientChips] = useState<EmailChip[]>([]);
  const [ccChips, setCcChips] = useState<EmailChip[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [attachments, setAttachments] = useState<File[]>([]);
  const [draftAttachments, setDraftAttachments] = useState<any[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");

  const [providers, setProviders] = useState<MailProvider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<MailProvider | null>(
    null
  );
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedColor, setSelectedColor] = useState("#000000");
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [selectedBgColor, setSelectedBgColor] = useState("#ffffff");
  const [showFormatDropdown, setShowFormatDropdown] = useState(false);
  const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false);
  const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());
  const [currentFontSize, setCurrentFontSize] = useState<string>("");
  const [currentFontFamily, setCurrentFontFamily] = useState<string>("");
  const [fontSizeDropdownPos, setFontSizeDropdownPos] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const fontSizeBtnRef = useRef<HTMLButtonElement>(null);
  const formatDropdownRef = useRef<HTMLDivElement>(null);
  const fontSizeDropdownRef = useRef<HTMLDivElement>(null);
  const formatDropdownContainerRef = useRef<HTMLDivElement>(null);

  const { auth } = useContext<any>(AuthContext);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const contentEditableRef = useRef<HTMLDivElement>(null);
  const [savedRange, setSavedRange] = useState<Range | null>(null);

  const [originalRecipientChips, setOriginalRecipientChips] = useState<EmailChip[]>([]);
  const [originalCcChips, setOriginalCcChips] = useState<EmailChip[]>([]);
  const [originalSubject, setOriginalSubject] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [originalAttachments, setOriginalAttachments] = useState<File[]>([]);
  const [hasUserChanges, setHasUserChanges] = useState(false);

  const checkForUserChanges = () => {
    const currentRecipientEmails = getAllEmails(recipientChips, recipientEmail);
    const currentCcEmails = getAllEmails(ccChips, ccEmail);
    const originalRecipientEmails = getAllEmails(originalRecipientChips, "");
    const originalCcEmails = getAllEmails(originalCcChips, "");

    const cleanCurrentContent = content
      .replace(/ data-start="[^"]*"/g, "")
      .replace(/ data-end="[^"]*"/g, "")
      .replace(/<[^>]*>/g, '')
      .trim();

    const cleanOriginalContent = originalContent
      .replace(/ data-start="[^"]*"/g, "")
      .replace(/ data-end="[^"]*"/g, "")
      .replace(/<[^>]*>/g, '')
      .trim();

    const hasRecipientChanges = JSON.stringify(currentRecipientEmails.sort()) !== JSON.stringify(originalRecipientEmails.sort());
    const hasCcChanges = JSON.stringify(currentCcEmails.sort()) !== JSON.stringify(originalCcEmails.sort());
    const hasSubjectChanges = subject.trim() !== originalSubject.trim();
    const hasContentChanges = cleanCurrentContent !== cleanOriginalContent;
    const hasAttachmentChanges = attachments.length !== originalAttachments.length;

    const hasChanges = hasRecipientChanges || hasCcChanges || hasSubjectChanges || hasContentChanges || hasAttachmentChanges;
    setHasUserChanges(hasChanges);
  };

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const createChipsFromEmail = (emailString: string): EmailChip[] => {
    if (!emailString.trim()) return [];

    return emailString
      .split(/[,;\n]/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0)
      .map((email) => ({
        email: email,
        isValid: isValidEmail(email),
      }));
  };

  const getAllEmails = (chips: EmailChip[], currentInput: string): string[] => {
    const chipEmails = chips
      .filter((chip) => chip.isValid)
      .map((chip) => chip.email);
    const inputEmails = currentInput.trim()
      ? currentInput
        .split(/[,;\n]/)
        .map((email) => email.trim())
        .filter((email) => email.length > 0 && isValidEmail(email))
      : [];
    return [...chipEmails, ...inputEmails];
  };

  const parseEmailAddresses = (emailString: string): string[] => {
    return emailString
      .split(/[,;\n]/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);
  };

  const validateEmailAddresses = (
    emails: string[]
  ): { valid: string[]; invalid: string[] } => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const valid: string[] = [];
    const invalid: string[] = [];

    emails.forEach((email) => {
      if (emailRegex.test(email)) {
        valid.push(email);
      } else {
        invalid.push(email);
      }
    });

    return { valid, invalid };
  };

  const handleRecipientChipCreation = () => {
    if (recipientEmail.trim()) {
      const newChips = createChipsFromEmail(recipientEmail);
      setRecipientChips((prev) => [...prev, ...newChips]);
      setRecipientEmail("");
    }
  };

  const handleCcChipCreation = () => {
    if (ccEmail.trim()) {
      const newChips = createChipsFromEmail(ccEmail);
      setCcChips((prev) => [...prev, ...newChips]);
      setCcEmail("");
    }
  };

  const handleRecipientKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRecipientChipCreation();
    }
  };

  const handleCcKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCcChipCreation();
    }
  };

  const removeRecipientChip = (index: number) => {

    if (readOnlyRecipient && recipientChips.length <= 1) {
      return;
    }
    setRecipientChips((prev) => prev.filter((_, i) => i !== index));
  };

  const removeCcChip = (index: number) => {
    setCcChips((prev) => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (isOpen && auth?.user_id) {
      if (cachedProviders && cachedProviders.length > 0) {
        setProviders(cachedProviders);
        if (!selectedProvider) setSelectedProvider(cachedProviders[0]);
      } else {
        fetchProviders();
      }
    }
  }, [isOpen, auth?.user_id, cachedProviders]);

  useEffect(() => {
    if (draftData && isOpen) {

      let recipientEmails = "";
      if (draftData.send_to) {
        try {

          const parsedEmails = JSON.parse(draftData.send_to);
          if (Array.isArray(parsedEmails)) {
            recipientEmails = parsedEmails.join(", ");
          } else {
            recipientEmails = draftData.send_to;
          }
        } catch (error) {

          try {
            const pyStyle = draftData.send_to.replace(/'/g, '"');
            const parsed = JSON.parse(pyStyle);
            if (Array.isArray(parsed)) {
              recipientEmails = parsed.join(", ");
            } else {
              recipientEmails = draftData.send_to;
            }
          } catch (pyError) {

            const cleaned = draftData.send_to.replace(/\[|\]|'|"/g, "");
            const parts = cleaned
              .split(",")
              .map((e: string) => e.trim())
              .filter(Boolean);
            if (parts.length > 1) {
              recipientEmails = parts.join(", ");
            } else {
              recipientEmails = cleaned;
            }
          }
        }
      }

      let ccEmails = "";
      if (
        draftData.mail_cc &&
        draftData.mail_cc !== "[]" &&
        draftData.mail_cc !== "[]"
      ) {
        try {

          const parsedCcEmails = JSON.parse(draftData.mail_cc);
          if (Array.isArray(parsedCcEmails) && parsedCcEmails.length > 0) {
            ccEmails = parsedCcEmails.join(", ");
          } else {

            ccEmails = "";
          }
        } catch (error) {

          try {
            const pyStyle = draftData.mail_cc.replace(/'/g, '"');
            const parsed = JSON.parse(pyStyle);
            if (Array.isArray(parsed) && parsed.length > 0) {
              ccEmails = parsed.join(", ");
            } else {

              ccEmails = "";
            }
          } catch (pyError) {

            const cleaned = draftData.mail_cc.replace(/\[|\]|'|"/g, "");
            const parts = cleaned
              .split(",")
              .map((e: string) => e.trim())
              .filter(Boolean);
            if (parts.length > 0) {
              ccEmails = parts.join(", ");
            } else {

              ccEmails = "";
            }
          }
        }
      }

      const recipientChipsData = createChipsFromEmail(recipientEmails);
      const ccChipsData = createChipsFromEmail(ccEmails);

      setRecipientChips(recipientChipsData);
      setCcChips(ccChipsData);
      setRecipientEmail("");
      setCcEmail("");
      setSubject(draftData.subject || "");
      setContent(draftData.content || "");

      setOriginalRecipientChips(recipientChipsData);
      setOriginalCcChips(ccChipsData);
      setOriginalSubject(draftData.subject || "");
      setOriginalContent(draftData.content || "");
      setOriginalAttachments([]);
      setHasUserChanges(false);

      if (draftData.attachments && draftData.attachments.length > 0) {
        setDraftAttachments(draftData.attachments);
      } else {
        setDraftAttachments([]);
      }
    } else if (!draftData && isOpen) {

      setRecipientEmail("");
      setCcEmail("");
      setRecipientChips([]);
      setCcChips([]);
      setSubject("");
      setContent("");
      setAttachments([]);
      setDraftAttachments([]);

      setOriginalRecipientChips([]);
      setOriginalCcChips([]);
      setOriginalSubject("");
      setOriginalContent("");
      setOriginalAttachments([]);
      setHasUserChanges(false);
    }
  }, [draftData, isOpen]);

  useEffect(() => {
    if (isOpen) {
      checkForUserChanges();
    }
  }, [recipientChips, recipientEmail, ccChips, ccEmail, subject, content, attachments]);

  useEffect(() => {
    if (
      isOpen &&
      contentEditableRef.current &&
      content !== contentEditableRef.current.innerHTML
    ) {
      contentEditableRef.current.innerHTML = content;
      setTimeout(() => {
        makeLinksClickable();
        if (contentEditableRef.current) {
          contentEditableRef.current.focus();
        }
      }, 50);
    }
  }, [content, isOpen]);

  useEffect(() => {
    if (contentEditableRef.current && isOpen) {
      setTimeout(() => {
        makeLinksClickable();

        checkFormatState();
      }, 100);
    }
  }, [isOpen, draftData]);

  const fetchProviders = async () => {
    try {
      setLoadingProviders(true);
      const userId = auth?.user_id?.toString() || "";
      const providersData = await EmailService.getMailProviders(userId);
      setProviders(providersData);

      if (providersData.length > 0 && !selectedProvider) {
        setSelectedProvider(providersData[0]);
      }
    } catch (error) {
      console.error("Error fetching providers:", error);
      showErrorAlert("Failed to load email providers");
    } finally {
      setLoadingProviders(false);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedProvider) {
      showErrorAlert("Please select an email provider");
      return;
    }

    const allRecipientEmails = getAllEmails(recipientChips, recipientEmail);
    if (allRecipientEmails.length === 0) {
      showErrorAlert("Please enter recipient email");
      return;
    }

    if (!subject.trim()) {
      showErrorAlert("Please enter subject");
      return;
    }

    if (!content.trim()) {
      showErrorAlert("Please enter email content");
      return;
    }

    try {
      setSendingEmail(true);
      const userId = auth?.user_id?.toString() || "";
      const cleanHtml = content
        .replace(/ data-start="[^"]*"/g, "")
        .replace(/ data-end="[^"]*"/g, "");

      const hasHtmlTags = /<[^>]+>/.test(cleanHtml);

      const toEmails = getAllEmails(recipientChips, recipientEmail);
      const ccEmails = getAllEmails(ccChips, ccEmail);

      const { valid: validToEmails, invalid: invalidToEmails } =
        validateEmailAddresses(toEmails);
      const { valid: validCcEmails, invalid: invalidCcEmails } =
        validateEmailAddresses(ccEmails);

      if (validToEmails.length === 0) {
        showErrorAlert(
          "Please enter at least one valid recipient email address"
        );
        return;
      }

      if (invalidToEmails.length > 0) {
        showErrorAlert(
          `Invalid recipient email(s): ${invalidToEmails.join(", ")}`
        );
        return;
      }

      if (invalidCcEmails.length > 0) {
        showErrorAlert(`Invalid CC email(s): ${invalidCcEmails.join(", ")}`);
        return;
      }

      const payload: SendEmailPayload = {
        to: validToEmails,
        subject: subject.trim(),
        body: cleanHtml,
        is_html: hasHtmlTags,
        user_id: userId,
        crm_entity_id,
        record_id,
      };

      if (validCcEmails.length > 0) {
        payload.cc = validCcEmails;
      }

      if (attachments.length > 0 || draftAttachments.length > 0) {

        const formData = new FormData();

        formData.append("to", JSON.stringify(payload.to));
        if (payload.cc) {
          formData.append("cc", JSON.stringify(payload.cc));
        }
        formData.append("subject", payload.subject);
        formData.append("body", payload.body);
        formData.append("is_html", payload.is_html.toString());
        formData.append("user_id", payload.user_id);
        if (payload.crm_entity_id) {
          formData.append("crm_entity_id", payload.crm_entity_id.toString());
        }
        if (payload.record_id) {
          formData.append("record_id", payload.record_id.toString());
        }

        if (draftAttachments.length > 0) {
          const draftAttachmentUrls = draftAttachments
            .map((att) => att.attachement_url)
            .filter(Boolean);
          if (draftAttachmentUrls.length > 0) {
            formData.append(
              "draft_attachment_urls",
              JSON.stringify(draftAttachmentUrls)
            );
          }
        }

        attachments.forEach((file) => {
          formData.append("attachments", file);
        });

        await EmailService.sendEmailWithBinaryAttachments(
          selectedProvider.mail_provider_key,
          formData
        );
      } else {

        await EmailService.sendEmail(
          selectedProvider.mail_provider_key,
          payload
        );
      }

      if (draftData && draftData.mail_message_id) {
        try {
          await EmailService.deleteEmail(draftData.mail_message_id);
        } catch (error) {
          console.error("Error deleting draft after sending:", error);

        }
      }

      showSuccessAlert("Email sent successfully!");

      setRecipientEmail("");
      setCcEmail("");
      setRecipientChips([]);
      setCcChips([]);
      setSubject("");
      setContent("");
      setAttachments([]);
      setDraftAttachments([]);
      setShowCc(false);

      onClose();
    } catch (error) {
      console.error("Error sending email:", error);
      showErrorAlert("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  const saveCurrentRange = () => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      setSavedRange(selection.getRangeAt(0).cloneRange());
    }
  };

  const restoreRange = () => {
    if (savedRange && contentEditableRef.current) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(savedRange);
      contentEditableRef.current.focus();
    }
  };

  const checkFormatState = () => {
    if (!contentEditableRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const element =
      container.nodeType === Node.TEXT_NODE
        ? container.parentElement
        : (container as HTMLElement);

    if (!element) return;

    const newActiveFormats = new Set<string>();

    if (
      element.style.fontWeight === "bold" ||
      element.tagName === "B" ||
      element.tagName === "STRONG"
    ) {
      newActiveFormats.add("bold");
    }
    if (
      element.style.fontStyle === "italic" ||
      element.tagName === "I" ||
      element.tagName === "EM"
    ) {
      newActiveFormats.add("italic");
    }
    if (
      element.style.textDecoration?.includes("underline") ||
      element.tagName === "U"
    ) {
      newActiveFormats.add("underline");
    }
    if (
      element.style.textDecoration?.includes("line-through") ||
      element.tagName === "S" ||
      element.tagName === "STRIKE"
    ) {
      newActiveFormats.add("strikethrough");
    }
    if (element.style.verticalAlign === "sub" || element.tagName === "SUB") {
      newActiveFormats.add("subscript");
    }
    if (element.style.verticalAlign === "super" || element.tagName === "SUP") {
      newActiveFormats.add("superscript");
    }

    const textAlign =
      (element as HTMLElement).style.textAlign ||
      window.getComputedStyle(element).textAlign;
    if (textAlign === "left") newActiveFormats.add("alignLeft");
    if (textAlign === "center") newActiveFormats.add("alignCenter");
    if (textAlign === "right") newActiveFormats.add("alignRight");
    if (textAlign === "justify") newActiveFormats.add("alignJustify");

    if (element.tagName === "UL" || element.closest("ul")) {
      newActiveFormats.add("unorderedList");
    }
    if (element.tagName === "OL" || element.closest("ol")) {
      newActiveFormats.add("orderedList");
    }

    const fontSize =
      (element as HTMLElement).style.fontSize ||
      window.getComputedStyle(element).fontSize;
    const fontFamily =
      (element as HTMLElement).style.fontFamily ||
      window.getComputedStyle(element).fontFamily;

    const sizeMap: { [key: string]: string } = {
      "8px": "1",
      "10px": "2",
      "12px": "3",
      "14px": "4",
      "18px": "5",
      "24px": "6",
      "36px": "7",
    };

    if (sizeMap[fontSize]) {
      setCurrentFontSize(sizeMap[fontSize]);
    }

    const familyMap: { [key: string]: string } = {
      Arial: "Arial",
      "Times New Roman": "Times New Roman",
      "Courier New": "Courier New",
      Georgia: "Georgia",
      Verdana: "Verdana",
      Helvetica: "Helvetica",
    };

    for (const [key, value] of Object.entries(familyMap)) {
      if (fontFamily.includes(key)) {
        setCurrentFontFamily(value);
        break;
      }
    }

    setActiveFormats(newActiveFormats);
  };

  const makeLinksClickable = () => {
    if (contentEditableRef.current) {
      const links = contentEditableRef.current.querySelectorAll("a");
      links.forEach((link) => {

        const newLink = link.cloneNode(true) as HTMLAnchorElement;

        let href = newLink.getAttribute("href") || newLink.textContent || "";
        if (
          href &&
          !href.startsWith("http://") &&
          !href.startsWith("https://")
        ) {
          href = `https://${href}`;
          newLink.href = href;
        }

        newLink.style.color = "#0066cc";
        newLink.style.textDecoration = "underline";
        newLink.style.cursor = "pointer";
        newLink.style.fontWeight = "normal";
        newLink.style.display = "inline";
        newLink.target = "_blank";
        newLink.rel = "noopener noreferrer";

        newLink.className = "compose-link";

        newLink.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const url = newLink.href;
          if (url) {
            window.open(url, "_blank", "noopener,noreferrer");
          }
        });

        newLink.addEventListener("mouseenter", () => {
          newLink.style.backgroundColor = "#f0f8ff";
          newLink.style.textDecoration = "underline";
        });

        newLink.addEventListener("mouseleave", () => {
          newLink.style.backgroundColor = "transparent";
          newLink.style.textDecoration = "underline";
        });

        newLink.addEventListener("dblclick", (e) => {
          e.preventDefault();
          e.stopPropagation();
        });

        if (link.parentNode) {
          link.parentNode.replaceChild(newLink, link);
        }
      });

      const textNodes = getTextNodes(contentEditableRef.current);
      textNodes.forEach((textNode) => {
        const text = textNode.textContent || "";
        const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
        const matches = text.match(urlRegex);

        if (matches) {
          let newHtml = text;
          matches.forEach((match) => {
            const url = match.startsWith("http") ? match : `https://${match}`;
            const linkHtml = `<a href="${url}" class="compose-link" target="_blank" rel="noopener noreferrer" style="color: #0066cc; text-decoration: underline; cursor: pointer;">${match}</a>`;
            newHtml = newHtml.replace(match, linkHtml);
          });

          if (newHtml !== text) {
            const span = document.createElement("span");
            span.innerHTML = newHtml;
            if (textNode.parentNode) {
              textNode.parentNode.replaceChild(span, textNode);

              setTimeout(() => makeLinksClickable(), 10);
            }
          }
        }
      });
    }
  };

  const getTextNodes = (element: HTMLElement): Text[] => {
    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);

    let node;
    while ((node = walker.nextNode())) {
      if (
        node.textContent &&
        node.textContent.trim() &&
        !node.parentElement?.tagName.toLowerCase().includes("a")
      ) {
        textNodes.push(node as Text);
      }
    }
    return textNodes;
  };

  const handleContentChange = (e: React.FormEvent<HTMLDivElement>) => {
    const newContent = e.currentTarget.innerHTML;
    setContent(newContent);
    saveCurrentRange();
    checkFormatState();

    setTimeout(() => {
      makeLinksClickable();

      saveCurrentRange();
    }, 50);
  };

  const handleLinkButtonClick = () => {
    saveCurrentRange();
    setShowLinkInput(true);
  };

  const handleInsertLink = () => {
    if (!linkUrl) return;

    if (contentEditableRef.current) {
      restoreRange();

      const link = document.createElement("a");

      const url = linkUrl.trim();
      const absoluteUrl =
        url.startsWith("http://") || url.startsWith("https://")
          ? url
          : `https://${url}`;

      link.href = absoluteUrl;
      link.textContent = linkText || linkUrl;
      link.className = "compose-link";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.style.color = "#0066cc";
      link.style.textDecoration = "underline";
      link.style.cursor = "pointer";
      link.style.fontWeight = "normal";
      link.style.display = "inline";

      link.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.open(absoluteUrl, "_blank", "noopener,noreferrer");
      });

      link.addEventListener("mouseenter", () => {
        link.style.textDecoration = "underline";
        link.style.backgroundColor = "#f0f8ff";
      });

      link.addEventListener("mouseleave", () => {
        link.style.textDecoration = "underline";
        link.style.backgroundColor = "transparent";
      });

      link.addEventListener("dblclick", (e) => {
        e.preventDefault();
        e.stopPropagation();
      });

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(link);

        const space = document.createTextNode(" ");
        range.setStartAfter(link);
        range.insertNode(space);

        range.setStartAfter(space);
        range.setEndAfter(space);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      setContent(contentEditableRef.current.innerHTML);
      contentEditableRef.current.focus();
    }

    setShowLinkInput(false);
    setLinkUrl("");
    setLinkText("");
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    if (contentEditableRef.current) {
      restoreRange();

      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(emojiData.emoji));

        range.setStartAfter(range.endContainer);
        range.setEndAfter(range.endContainer);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      contentEditableRef.current.focus();
    }

    setShowEmojiPicker(false);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    onMinimize();
  };

  const handleFileClick = () => {
    if (attachments.length + draftAttachments.length >= 5) {
      showErrorAlert("Maximum file limit reached! You can only upload up to 5 files. Please remove some existing files before adding new ones.");
      return;
    }

    if (fileInputRef.current) {

      fileInputRef.current.value = "";
      fileInputRef.current.click();
    } else {
      console.error("File input ref is null");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    if (files.length === 0) return;

    const maxFileSize = 10 * 1024 * 1024;
    const maxFiles = 5;
    const allowedTypes = [".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt", ".gif", ".bmp", ".zip", ".rar", ".ppt", ".pptx"];

    const currentFileCount = attachments.length;
    const draftAttachmentsCount = draftAttachments.length;
    const totalCurrentFiles = currentFileCount + draftAttachmentsCount;

    if (totalCurrentFiles + files.length > maxFiles) {
      const remainingSlots = maxFiles - totalCurrentFiles;
      if (remainingSlots <= 0) {
        showErrorAlert(`Maximum file limit reached! You can only upload up to ${maxFiles} files. Please remove some existing files before adding new ones.`);
        return;
      } else {
        showErrorAlert(`You can only add ${remainingSlots} more file(s). You currently have ${totalCurrentFiles} files.`);
        return;
      }
    }

    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();

      if (file.size > maxFileSize) {
        errors.push(`${file.name} is too large. Maximum size is 10MB.`);
      } else if (!allowedTypes.includes(fileExtension)) {
        errors.push(`${file.name} is not allowed. Only ${allowedTypes.join(", ")} files are permitted.`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      showErrorAlert(errors.join(", "));
      return;
    }

    if (validFiles.length > 0) {
      setAttachments((prev) => {
        const newAttachments = [...prev, ...validFiles];
        return newAttachments;
      });
    }

    if (e.target) {
      e.target.value = "";
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveDraftAttachment = (index: number) => {
    setDraftAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const saveDraft = async () => {

    const allRecipientEmails = getAllEmails(recipientChips, recipientEmail);
    const allCcEmails = getAllEmails(ccChips, ccEmail);

    const cleanHtml = content
      .replace(/ data-start="[^"]*"/g, "")
      .replace(/ data-end="[^"]*"/g, "")
      .replace(/<[^>]*>/g, '')
      .trim();

    const hasRecipients = allRecipientEmails.length > 0;
    const hasCc = allCcEmails.length > 0;
    const hasSubject = subject.trim().length > 0;
    const hasContent = cleanHtml.length > 0;
    const hasNewAttachments = attachments.length > 0;
    const hasDraftAttachments = draftAttachments.length > 0;

    if (!hasRecipients && !hasCc && !hasSubject && !hasContent && !hasNewAttachments) {
      return;
    }

    if (!hasUserChanges) {
      return;
    }

    if (!selectedProvider) {
      return;
    }

    try {
      const userId = auth?.user_id?.toString() || "";
      if (!userId) return;

      const cleanHtml = content
        .replace(/ data-start="[^"]*"/g, "")
        .replace(/ data-end="[^"]*"/g, "");

      const hasHtmlTags = /<[^>]+>/.test(cleanHtml);

      const toEmails = getAllEmails(recipientChips, recipientEmail);
      const ccEmails = getAllEmails(ccChips, ccEmail);

      const payload: SendEmailPayload = {
        to: toEmails,
        subject: subject.trim(),
        body: cleanHtml,
        is_html: hasHtmlTags,
        user_id: userId,
      };

      if (ccEmails.length > 0) {
        payload.cc = ccEmails;
      }

      if (attachments.length > 0 || draftAttachments.length > 0) {

        const formData = new FormData();

        formData.append("to", JSON.stringify(payload.to));
        if (payload.cc) {
          formData.append("cc", JSON.stringify(payload.cc));
        }
        formData.append("subject", payload.subject);
        formData.append("body", payload.body);
        formData.append("is_html", payload.is_html.toString());
        formData.append("user_id", payload.user_id);
        formData.append(
          "mail_message_id",
          draftData && draftData.mail_message_id
            ? draftData.mail_message_id.toString()
            : "null"
        );

        if (draftAttachments.length > 0) {
          const draftAttachmentUrls = draftAttachments
            .map((att) => att.attachement_url)
            .filter(Boolean);
          if (draftAttachmentUrls.length > 0) {
            formData.append(
              "draft_attachment_urls",
              JSON.stringify(draftAttachmentUrls)
            );
          }
        }

        attachments.forEach((file) => {
          formData.append("attachments", file);
        });

        await EmailService.sendDraftWithBinaryAttachments(formData);
      } else {
        (payload as any).mail_message_id =
          draftData && draftData.mail_message_id
            ? draftData.mail_message_id
            : null;
        await EmailService.sendDraft(
          selectedProvider.mail_provider_key,
          payload
        );
      }
    } catch (error) {
      console.error("Error saving draft:", error);

    }
  };

  const handleDelete = () => {
    setRecipientEmail("");
    setCcEmail("");
    setRecipientChips([]);
    setCcChips([]);
    setSubject("");
    setContent("");
    setAttachments([]);
    setDraftAttachments([]);
    onClose();
  };

  const handleClose = async () => {

    const allRecipientEmails = getAllEmails(recipientChips, recipientEmail);
    const allCcEmails = getAllEmails(ccChips, ccEmail);

    const cleanHtml = content
      .replace(/ data-start="[^"]*"/g, "")
      .replace(/ data-end="[^"]*"/g, "")
      .replace(/<[^>]*>/g, '')
      .trim();

    const hasRecipients = allRecipientEmails.length > 0;
    const hasCc = allCcEmails.length > 0;
    const hasSubject = subject.trim().length > 0;
    const hasContent = cleanHtml.length > 0;
    const hasNewAttachments = attachments.length > 0;

    const hasAnyField = hasRecipients || hasCc || hasSubject || hasContent || hasNewAttachments;

    if (hasAnyField && selectedProvider && hasUserChanges) {
      try {
        const userId = auth?.user_id?.toString() || "";
        const cleanHtml = content
          .replace(/ data-start="[^"]*"/g, "")
          .replace(/ data-end="[^"]*"/g, "");

        const hasHtmlTags = /<[^>]+>/.test(cleanHtml);

        const toEmails = getAllEmails(recipientChips, recipientEmail);
        const ccEmails = getAllEmails(ccChips, ccEmail);

        const payload: SendEmailPayload = {
          to: toEmails,
          subject: subject.trim(),
          body: cleanHtml,
          is_html: hasHtmlTags,
          user_id: userId,
        };

        if (ccEmails.length > 0) {
          payload.cc = ccEmails;
        }

        if (attachments.length > 0 || draftAttachments.length > 0) {

          const formData = new FormData();

          formData.append("to", JSON.stringify(payload.to));
          if (payload.cc) {
            formData.append("cc", JSON.stringify(payload.cc));
          }
          formData.append("subject", payload.subject);
          formData.append("body", payload.body);
          formData.append("is_html", payload.is_html.toString());
          formData.append("user_id", payload.user_id);
          formData.append(
            "mail_message_id",
            draftData && draftData.mail_message_id
              ? draftData.mail_message_id.toString()
              : "null"
          );

          if (draftAttachments.length > 0) {
            const draftAttachmentUrls = draftAttachments
              .map((att) => att.attachement_url)
              .filter(Boolean);
            if (draftAttachmentUrls.length > 0) {
              formData.append(
                "draft_attachment_urls",
                JSON.stringify(draftAttachmentUrls)
              );
            }
          }

          attachments.forEach((file) => {
            formData.append("attachments", file);
          });

          await EmailService.sendDraftWithBinaryAttachments(formData);
        } else {
          (payload as any).mail_message_id =
            draftData && draftData.mail_message_id
              ? draftData.mail_message_id
              : null;
          await EmailService.sendDraft(
            selectedProvider.mail_provider_key,
            payload
          );
        }
      } catch (error) {
        console.error("Error saving draft when closing:", error);

      }
    }
    onClose();
  };

  const execCommand = (command: string, value?: string) => {
    saveCurrentRange();
    restoreRange();
    document.execCommand(command, false, value);
    contentEditableRef.current?.focus();
  };

  const handleFormatAction = (action: string, value?: string) => {
    switch (action) {
      case "bold":
        execCommand("bold");
        break;
      case "italic":
        execCommand("italic");
        break;
      case "underline":
        execCommand("underline");
        break;
      case "strikethrough":
        execCommand("strikethrough");
        break;
      case "fontSize":
        execCommand("fontSize", value);
        if (value) setCurrentFontSize(value);
        break;
      case "fontName":
        execCommand("fontName", value);
        if (value) setCurrentFontFamily(value);
        break;
      case "justifyLeft":
        execCommand("justifyLeft");
        break;
      case "justifyCenter":
        execCommand("justifyCenter");
        break;
      case "justifyRight":
        execCommand("justifyRight");
        break;
      case "justifyFull":
        execCommand("justifyFull");
        break;
      case "insertUnorderedList":
        execCommand("insertUnorderedList");
        break;
      case "insertOrderedList":
        execCommand("insertOrderedList");
        break;
      case "foreColor":
        execCommand("foreColor", value);
        break;
      case "backColor":
        execCommand("backColor", value);
        break;
      case "removeFormat":
        execCommand("removeFormat");
        setActiveFormats(new Set());
        setCurrentFontSize("");
        setCurrentFontFamily("");
        break;
      case "subscript":
        execCommand("subscript");
        break;
      case "superscript":
        execCommand("superscript");
        break;
      case "indent":
        execCommand("indent");
        break;
      case "outdent":
        execCommand("outdent");
        break;
    }

    setTimeout(() => {
      checkFormatState();
    }, 10);
  };

  const closeAllDropdowns = () => {
    setShowFormatDropdown(false);
    setShowFontSizeDropdown(false);
    setShowColorPicker(false);
    setShowBgColorPicker(false);
  };

  const handleColorChange = (color: string) => {
    setSelectedColor(color);
    handleFormatAction("foreColor", color);
    setShowColorPicker(false);
  };

  const handleBgColorChange = (color: string) => {
    setSelectedBgColor(color);
    handleFormatAction("backColor", color);
    setShowBgColorPicker(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      if (!target.closest(".color-picker-container")) {
        setShowColorPicker(false);
        setShowBgColorPicker(false);
      }

      const isFormatDropdownClick =
        target.closest(".format-dropdown-container") ||
        target.closest(".format-dropdown") ||
        target.closest(".font-size-container") ||
        target.closest(".font-size-dropdown") ||
        target.closest(".color-picker-container");

      if (!isFormatDropdownClick) {
        closeAllDropdowns();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeAllDropdowns();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    let formatTimeout: NodeJS.Timeout;
    let fontSizeTimeout: NodeJS.Timeout;

    const handleFormatMouseEnter = () => {
      clearTimeout(formatTimeout);
      setShowFormatDropdown(true);
    };

    const handleFormatMouseLeave = () => {
      formatTimeout = setTimeout(() => {
        setShowFormatDropdown(false);
      }, 1000);
    };

    const handleFontSizeMouseEnter = () => {
      clearTimeout(fontSizeTimeout);
      setShowFontSizeDropdown(true);
    };

    const handleFontSizeMouseLeave = () => {
      fontSizeTimeout = setTimeout(() => {
        setShowFontSizeDropdown(false);
      }, 1000);
    };

    if (formatDropdownContainerRef.current) {
      formatDropdownContainerRef.current.addEventListener(
        "mouseenter",
        handleFormatMouseEnter
      );
      formatDropdownContainerRef.current.addEventListener(
        "mouseleave",
        handleFormatMouseLeave
      );
    }

    if (formatDropdownRef.current) {
      formatDropdownRef.current.addEventListener(
        "mouseenter",
        handleFormatMouseEnter
      );
      formatDropdownRef.current.addEventListener(
        "mouseleave",
        handleFormatMouseLeave
      );
    }

    if (fontSizeDropdownRef.current) {
      fontSizeDropdownRef.current.addEventListener(
        "mouseenter",
        handleFontSizeMouseEnter
      );
      fontSizeDropdownRef.current.addEventListener(
        "mouseleave",
        handleFontSizeMouseLeave
      );
    }

    return () => {
      clearTimeout(formatTimeout);
      clearTimeout(fontSizeTimeout);

      if (formatDropdownContainerRef.current) {
        formatDropdownContainerRef.current.removeEventListener(
          "mouseenter",
          handleFormatMouseEnter
        );
        formatDropdownContainerRef.current.removeEventListener(
          "mouseleave",
          handleFormatMouseLeave
        );
      }

      if (formatDropdownRef.current) {
        formatDropdownRef.current.removeEventListener(
          "mouseenter",
          handleFormatMouseEnter
        );
        formatDropdownRef.current.removeEventListener(
          "mouseleave",
          handleFormatMouseLeave
        );
      }

      if (fontSizeDropdownRef.current) {
        fontSizeDropdownRef.current.removeEventListener(
          "mouseenter",
          handleFontSizeMouseEnter
        );
        fontSizeDropdownRef.current.removeEventListener(
          "mouseleave",
          handleFontSizeMouseLeave
        );
      }
    };
  }, [showFormatDropdown, showFontSizeDropdown]);

  if (!isOpen) return null;

  return (
    <>
      <style>
        {`
          .compose-editable a,
          .compose-editable a.compose-link {
            color: #0066cc !important;
            text-decoration: underline !important;
            cursor: pointer !important;
            font-weight: normal !important;
            display: inline !important;
            border: none !important;
            background: transparent !important;
            outline: none !important;
          }
          .compose-editable a:hover,
          .compose-editable a.compose-link:hover {
            background-color: #f0f8ff !important;
            text-decoration: underline !important;
          }
          .compose-editable a:focus,
          .compose-editable a.compose-link:focus {
            outline: 1px dotted #0066cc !important;
            background-color: #f0f8ff !important;
          }
          .compose-editable a:active,
          .compose-editable a.compose-link:active {
            background-color: #e6f3ff !important;
          }
        `}
      </style>
      <div
        className={`compose-email-wrapper ${isFullScreen ? "fullscreen" : ""} ${isMinimized ? "minimized" : ""
          }`}
      >
        <div className="compose-email">
          <div className="compose-header">
            <div className="compose-title">
              {draftData ? "Edit Draft" : "New Message"}
            </div>
            <div className="compose-actions">
              { }
              <button
                className="btn btn-icon"
                onClick={() => setIsFullScreen(!isFullScreen)}
                title={isFullScreen ? "Shrink View" : "Expand View"}
              >
                {isFullScreen ? (
                  <i className="fas fa-compress-alt"></i>
                ) : (
                  <i className="fas fa-expand-alt"></i>
                )}
              </button>
              <button
                className="btn btn-icon"
                onClick={handleClose}
                title="Close"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>

          <div className="compose-body">
            <div className="compose-field">
              <select
                value={selectedProvider?.mail_provider_id ?? ""}
                onChange={(e) => {
                  const provider = providers.find(
                    (p) => p.mail_provider_id.toString() === e.target.value
                  );
                  setSelectedProvider(provider || null);
                }}
                className="compose-input"
                disabled={loadingProviders}
              >
                {loadingProviders ? (
                  <option value="">Loading accounts...</option>
                ) : providers.length === 0 ? (
                  <option value="">No accounts available</option>
                ) : (
                  <>
                    <option value="">Select Account</option>
                    {providers.map((provider) => (
                      <option
                        key={provider.mail_provider_id}
                        value={provider.mail_provider_id}
                      >
                        {provider.mail_provider_display_name
                          .charAt(0)
                          .toUpperCase() +
                          provider.mail_provider_display_name.slice(1)}
                        {provider.email ? ` (${provider.email})` : ""}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>

            <div className="compose-field" style={{ marginBottom: readOnlyRecipient ? "0px" : undefined }}>
              <div className="compose-field-with-action d-flex align-items-start" style={{
                marginBottom: readOnlyRecipient ? "0px" : undefined,
                paddingBottom: readOnlyRecipient ? "0px" : undefined,
              }}>
                <span
                  className="text-muted fw-medium me-2"
                  style={{
                    minWidth: "30px",
                    paddingTop: readOnlyRecipient ? "4px" : "8px",
                    marginBottom: readOnlyRecipient ? "0px" : undefined
                  }}
                >
                  To:
                </span>
                <div
                  className="email-input-container d-flex flex-wrap align-items-center gap-1"
                  style={{
                    flex: "1",
                    width: "100%",
                    maxWidth: "calc(100% - 70px)",
                    padding: readOnlyRecipient ? "0px" : undefined,
                    minHeight: readOnlyRecipient ? "auto" : undefined,
                    gap: readOnlyRecipient ? "0px" : undefined,
                    margin: readOnlyRecipient ? "0px" : undefined,
                  }}
                >
                  { }
                  {recipientChips.map((chip, index) => (
                    <span
                      key={index}
                      className={`email-chip ${chip.isValid ? "valid" : "invalid"
                        }`}
                      style={{
                        margin: readOnlyRecipient ? "0px" : undefined,
                        padding: readOnlyRecipient ? "2px 6px" : undefined,
                      }}
                    >
                      {chip.email}
                      {(!readOnlyRecipient || (readOnlyRecipient && recipientChips.length > 1)) && (
                        <button
                          className="chip-remove"
                          onClick={() => removeRecipientChip(index)}
                          type="button"
                          title={readOnlyRecipient && recipientChips.length === 1 ? "Cannot remove the last remaining email" : "Remove email"}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ))}
                  {(!readOnlyRecipient || recipientEmail || recipientChips.length > 0) && (
                    <input
                      type="text"
                      placeholder={readOnlyRecipient ? "" : "Enter Email Addresses"}
                      value={recipientEmail ?? ""}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      onKeyPress={handleRecipientKeyPress}
                      onBlur={handleRecipientChipCreation}
                      className="compose-input chip-input"
                      style={{
                        border: "none",
                        outline: "none",
                        flex: readOnlyRecipient ? "0 0 auto" : "1 1 auto",
                        minWidth: readOnlyRecipient ? "0px" : "120px",
                        maxWidth: "100%",
                        display: readOnlyRecipient && !recipientEmail && recipientChips.length === 0 ? "none" : "block",
                        padding: readOnlyRecipient ? "0px" : undefined,
                        height: readOnlyRecipient ? "auto" : undefined,
                        lineHeight: readOnlyRecipient ? "1" : undefined,
                      }}
                      title={readOnlyRecipient ? "Recipient email (read-only)" : "Enter recipient email addresses (separate multiple emails with commas or semicolons, or press Enter)"}
                      readOnly={readOnlyRecipient}
                      disabled={readOnlyRecipient}
                    />
                  )}
                </div>
                {!showCc && !readOnlyRecipient && (
                  <button
                    className="btn btn-text"
                    onClick={() => setShowCc(true)}
                    title="Add Cc"
                    style={{ paddingTop: "8px", flexShrink: 0 }}
                  >
                    Cc
                  </button>
                )}
              </div>
            </div>

            {showCc && !readOnlyRecipient && (
              <div className="compose-field">
                <div className="compose-field-with-action d-flex align-items-start">
                  <span
                    className="text-muted fw-medium me-2"
                    style={{ minWidth: "30px", paddingTop: "8px" }}
                  >
                    Cc:
                  </span>
                  <div
                    className="email-input-container d-flex flex-wrap align-items-center gap-1"
                    style={{
                      flex: "1",
                      width: "100%",
                      maxWidth: "calc(100% - 80px)",
                    }}
                  >
                    { }
                    {ccChips.map((chip, index) => (
                      <span
                        key={index}
                        className={`email-chip ${chip.isValid ? "valid" : "invalid"
                          }`}
                      >
                        {chip.email}
                        <button
                          className="chip-remove"
                          onClick={() => removeCcChip(index)}
                          type="button"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      placeholder="Enter Cc Email Addresses"
                      value={ccEmail ?? ""}
                      onChange={(e) => setCcEmail(e.target.value)}
                      onKeyPress={handleCcKeyPress}
                      onBlur={handleCcChipCreation}
                      className="compose-input chip-input"
                      style={{
                        border: "none",
                        outline: "none",
                        flex: "1 1 auto",
                        minWidth: "120px",
                        maxWidth: "100%",
                      }}
                      title="Enter CC email addresses (separate multiple emails with commas or semicolons, or press Enter)"
                    />
                  </div>
                  <button
                    className="btn btn-text"
                    onClick={() => setShowCc(false)}
                    title="Remove Cc"
                    style={{ paddingTop: "8px", flexShrink: 0 }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
            )}

            <div className="compose-field">
              <input
                type="text"
                placeholder="Subject"
                value={subject ?? ""}
                onChange={(e) => setSubject(e.target.value)}
                className="compose-input"
              />
            </div>

            <div className="compose-content">
              <div
                ref={contentEditableRef}
                className="compose-editable"
                contentEditable
                onInput={handleContentChange}
                onClick={(e) => {
                  saveCurrentRange();
                  checkFormatState();
                }}
                onKeyUp={() => {
                  saveCurrentRange();
                  checkFormatState();
                }}
                onPaste={(e) => {

                  setTimeout(() => {
                    makeLinksClickable();

                    if (contentEditableRef.current) {
                      setContent(contentEditableRef.current.innerHTML);
                    }
                    checkFormatState();
                  }, 100);
                }}
                suppressContentEditableWarning
              />
            </div>

            {(attachments.length > 0 || draftAttachments.length > 0) && (
              <AttachmentComponent
                attachments={[
                  ...draftAttachments.map((att, index) => ({
                    id: `draft-${index}`,
                    name: att.attachement_url
                      ? att.attachement_url.split("/").pop() || `Draft Attachment ${index + 1}`
                      : `Draft Attachment ${index + 1}`,
                    size: att.size_in_kb ? att.size_in_kb * 1024 : 0,
                    type: '',
                    url: att.attachement_url,
                    created_at: '',
                    created_by: 0
                  })),
                  ...attachments.map((file, index) => ({
                    id: `new-${index}`,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    url: '',
                    created_at: '',
                    created_by: 0
                  }))
                ]}
                allowUpload={false}
                showActions={true}
                onDelete={(attachment: any, index: number) => {
                  if ('id' in attachment && typeof attachment.id === 'string' && attachment.id.startsWith('draft-')) {
                    const draftIndex = parseInt(attachment.id.replace('draft-', ''));
                    handleRemoveDraftAttachment(draftIndex);
                  } else if ('id' in attachment && typeof attachment.id === 'string' && attachment.id.startsWith('new-')) {
                    const newIndex = parseInt(attachment.id.replace('new-', ''));
                    handleRemoveAttachment(newIndex);
                  }
                }}
                variant="compact"
                className="mb-3"
              />
            )}
          </div>

          <div className="compose-footer">
            <div className="compose-main-actions">
              <button
                className="btn btn-primary me-2"
                onClick={handleSendEmail}
                disabled={sendingEmail || !selectedProvider}
              >
                <i className="ti ti-send me-2" />
                {sendingEmail ? "Sending..." : "Send"}
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.bmp,.zip,.rar,.xls,.xlsx,.ppt,.pptx"
                style={{ display: "none" }}
              />
              <button
                className="btn btn-icon"
                title={attachments.length + draftAttachments.length >= 5 ? "Maximum file limit reached (5 files)" : "Attach files"}
                onClick={handleFileClick}
              >
                <i className="fas fa-paperclip"></i>
                {(attachments.length > 0 || draftAttachments.length > 0) && (
                  <span className="attachment-count">
                    {attachments.length + draftAttachments.length}
                  </span>
                )}
              </button>
              <button
                className="btn btn-icon"
                title="Insert link"
                onClick={handleLinkButtonClick}
              >
                <i className="fas fa-link"></i>
              </button>
              <button
                className={`btn btn-icon ${activeFormats.has("bold") ? "active" : ""
                  }`}
                title="Bold"
                onClick={() => handleFormatAction("bold")}
              >
                <i className="fas fa-bold"></i>
              </button>
              <button
                className={`btn btn-icon ${activeFormats.has("italic") ? "active" : ""
                  }`}
                title="Italic"
                onClick={() => handleFormatAction("italic")}
              >
                <i className="fas fa-italic"></i>
              </button>
              <button
                className={`btn btn-icon ${activeFormats.has("underline") ? "active" : ""
                  }`}
                title="Underline"
                onClick={() => handleFormatAction("underline")}
              >
                <i className="fas fa-underline"></i>
              </button>
              <div
                className="font-size-container"
                onMouseEnter={() => setShowFontSizeDropdown(true)}
                onMouseLeave={() =>
                  setTimeout(() => setShowFontSizeDropdown(false), 1000)
                }
              >
                <button
                  className="btn btn-icon font-size-btn"
                  title="Font Size"
                  ref={fontSizeBtnRef}
                  id="font-size-btn"
                >
                  <i className="fas fa-text-height"></i>
                </button>
                {showFontSizeDropdown && (
                  <div
                    ref={fontSizeDropdownRef}
                    className="font-size-dropdown"
                    onMouseEnter={() => setShowFontSizeDropdown(true)}
                    onMouseLeave={() =>
                      setTimeout(() => setShowFontSizeDropdown(false), 1000)
                    }
                  >
                    <button
                      className={`btn btn-icon btn-sm ${currentFontSize === "1" ? "active" : ""
                        }`}
                      title="Small"
                      onClick={() => handleFormatAction("fontSize", "1")}
                    >
                      <span style={{ fontSize: "10px" }}>S</span>
                    </button>
                    <button
                      className={`btn btn-icon btn-sm ${currentFontSize === "3" ? "active" : ""
                        }`}
                      title="Normal"
                      onClick={() => handleFormatAction("fontSize", "3")}
                    >
                      <span style={{ fontSize: "12px" }}>N</span>
                    </button>
                    <button
                      className={`btn btn-icon btn-sm ${currentFontSize === "5" ? "active" : ""
                        }`}
                      title="Large"
                      onClick={() => handleFormatAction("fontSize", "5")}
                    >
                      <span style={{ fontSize: "16px" }}>L</span>
                    </button>
                    <button
                      className={`btn btn-icon btn-sm ${currentFontSize === "7" ? "active" : ""
                        }`}
                      title="Huge"
                      onClick={() => handleFormatAction("fontSize", "7")}
                    >
                      <span style={{ fontSize: "20px" }}>H</span>
                    </button>
                  </div>
                )}
              </div>

              <div className="emoji-picker-container">
                <button
                  className="btn btn-icon"
                  title="Insert emoji"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                >
                  <i className="far fa-smile"></i>
                </button>
                {showEmojiPicker && (
                  <div className="emoji-picker-wrapper">
                    <EmojiPicker onEmojiClick={handleEmojiClick} />
                  </div>
                )}
              </div>
              <div
                ref={formatDropdownContainerRef}
                className="format-dropdown-container"
              >
                <button
                  className="btn btn-icon"
                  title="More formatting options"
                >
                  <i className="fas fa-font"></i>
                </button>
                {showFormatDropdown && (
                  <div ref={formatDropdownRef} className="format-dropdown">
                    <div className="format-row">
                      <button
                        className={`btn btn-icon btn-sm ${activeFormats.has("strikethrough") ? "active" : ""
                          }`}
                        title="Strikethrough"
                        onClick={() => handleFormatAction("strikethrough")}
                      >
                        <i className="fas fa-strikethrough"></i>
                      </button>
                      <button
                        className={`btn btn-icon btn-sm ${activeFormats.has("alignLeft") ? "active" : ""
                          }`}
                        title="Align Left"
                        onClick={() => handleFormatAction("justifyLeft")}
                      >
                        <i className="fas fa-align-left"></i>
                      </button>
                      <button
                        className={`btn btn-icon btn-sm ${activeFormats.has("alignCenter") ? "active" : ""
                          }`}
                        title="Align Center"
                        onClick={() => handleFormatAction("justifyCenter")}
                      >
                        <i className="fas fa-align-center"></i>
                      </button>
                      <button
                        className={`btn btn-icon btn-sm ${activeFormats.has("alignRight") ? "active" : ""
                          }`}
                        title="Align Right"
                        onClick={() => handleFormatAction("justifyRight")}
                      >
                        <i className="fas fa-align-right"></i>
                      </button>
                      <button
                        className={`btn btn-icon btn-sm ${activeFormats.has("alignJustify") ? "active" : ""
                          }`}
                        title="Justify"
                        onClick={() => handleFormatAction("justifyFull")}
                      >
                        <i className="fas fa-align-justify"></i>
                      </button>
                      <button
                        className={`btn btn-icon btn-sm ${activeFormats.has("unorderedList") ? "active" : ""
                          }`}
                        title="Bullet List"
                        onClick={() =>
                          handleFormatAction("insertUnorderedList")
                        }
                      >
                        <i className="fas fa-list-ul"></i>
                      </button>
                      <button
                        className={`btn btn-icon btn-sm ${activeFormats.has("orderedList") ? "active" : ""
                          }`}
                        title="Numbered List"
                        onClick={() => handleFormatAction("insertOrderedList")}
                      >
                        <i className="fas fa-list-ol"></i>
                      </button>
                      <button
                        className="btn btn-icon btn-sm"
                        title="Indent"
                        onClick={() => handleFormatAction("indent")}
                      >
                        <i className="fas fa-indent"></i>
                      </button>
                      <button
                        className="btn btn-icon btn-sm"
                        title="Outdent"
                        onClick={() => handleFormatAction("outdent")}
                      >
                        <i className="fas fa-outdent"></i>
                      </button>
                      <button
                        className="btn btn-icon btn-sm"
                        title="Remove Formatting"
                        onClick={() => handleFormatAction("removeFormat")}
                      >
                        <i className="fas fa-eraser"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="compose-secondary-actions">
              <button
                className="btn btn-icon"
                title="Delete draft"
                onClick={onClose}
              >
                <i className="far fa-trash-alt"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      { }
      {showLinkInput && (
        <div className="modal-backdrop">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Insert Link</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowLinkInput(false);
                    setLinkUrl("");
                    setLinkText("");
                  }}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="linkText" className="form-label">
                    Text to display
                  </label>
                  <input
                    type="text"
                    id="linkText"
                    className="form-control"
                    placeholder="Link text"
                    value={linkText ?? ""}
                    onChange={(e) => setLinkText(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="linkUrl" className="form-label">
                    URL
                  </label>
                  <input
                    type="text"
                    id="linkUrl"
                    className="form-control"
                    placeholder="https://"
                    value={linkUrl ?? ""}
                    onChange={(e) => setLinkUrl(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowLinkInput(false);
                    setLinkUrl("");
                    setLinkText("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleInsertLink}
                  disabled={!linkUrl}
                >
                  Insert Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      { }
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .email-chip {
            display: inline-flex;
            align-items: center;
            background-color: #e3f2fd;
            border: 1px solid #2196f3;
            border-radius: 16px;
            padding: 4px 8px;
            margin: 2px 2px 2px 0;
            font-size: 12px;
            color: #1976d2;
            flex-shrink: 0;
            max-width: calc(100% - 4px);
            overflow: hidden;
          }
          
          .email-chip.valid {
            background-color: #e8f5e8;
            border-color: #4caf50;
            color: #2e7d32;
          }
          
          .email-chip.invalid {
            background-color: #ffebee;
            border-color: #f44336;
            color: #c62828;
          }
          
          .chip-remove {
            background: none;
            border: none;
            color: inherit;
            font-size: 14px;
            margin-left: 4px;
            cursor: pointer;
            padding: 0;
            line-height: 1;
          }
          
          .chip-remove:hover {
            opacity: 0.7;
          }
          
          .chip-input {
            background: transparent;
            border: none;
            outline: none;
            font-size: 12px;
            min-width: 120px;
          }
          
          .email-input-container {
            border: 1px solid #e9ecef;
            border-radius: 4px;
            padding: 4px 8px;
            min-height: 36px;
            background-color: #fff;
            width: 100% !important;
            box-sizing: border-box;
          }
          
          .email-input-container:focus-within {
            border-color: #007bff;
            box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
          }
          
          .compose-field-with-action {
            width: 100%;
            max-width: none;
            box-sizing: border-box;
          }
        `,
        }}
      />
    </>
  );
};

export default ComposeEmail;
